import { globalEvent } from '@shared/easyfy.core';
import { parseToBoolean, isMobile, addCss, removeCss, debounce } from '@shared/easyfy.utils';
import Kevel, { KevelContainerSelector } from '@clientWebModules/kevel';
import { initKevelSearchSuggestionAd } from '@clientWebModules/kevelSearchSuggestionAd';
import { HayppFeatureNames } from '@clientShared/HayppFeatureNames';
import { applyPreSearchTerm, setTrackedSearchInput, trackSearchInitiation } from '@clientWebModules/searchTracking';
import { Features } from '@webServices/features.service';
import EasyfyModules from '@shared/easyfy.events/easyfy.eventhandler';
import ProductListComponent from '../../dynamiccomponents/product/productlistcomponent';
import { OnGetFocusInData, OnSearch } from '@clientWebServices/hayppsearch.service';
import { announceSearchResult, clearSearchResultAnnouncement } from '@clientWebModules/searchResultAnnouncer';
import { logCustomMessage } from '@shared/easyfy.logger';
import { isDesktop } from '@clientShared/utils';
import { initSearchPlaceholderAnimations } from '@clientShared/searchPlaceholderAnimation';
import { initHorizontalScrollFade } from '@clientShared/horizontalScrollFade';
import { dismissKeyboardOnDrag } from '@clientShared/dismissKeyboardOnDrag';
import { hideDecorativeEmoji } from '@clientWebModules/decorativeEmoji';

export class SearchConfig {
  constructor(thresholdVolume: number = 2, displayStyle: string = 'block') {
    this.ThresholdVolume = thresholdVolume;
    this.DisplayStyle = displayStyle;
  }

  public ThresholdVolume: number;
  public DisplayStyle: string;
}

export default class Search {
  static Instance: Search;

  private backdrop: HTMLElement | null = null;
  private searchForm: HTMLFormElement | null = null;
  private searchInput: HTMLInputElement | null = null;
  private searchResults: HTMLElement | null = null;
  private searchBarElement: HTMLElement | null = null;
  private popularResults: HTMLElement | null = null;

  private lastSearchString: string | null = null;
  private pendingSearch: string | null = null;
  private focusInPopularData: string | null = null;
  private focusInDataRequest: Promise<void> | null = null;

  private initialized: boolean = false;

  private thresholdVolume: number = 2;
  private showPopularResultsOnFocusIn: boolean = false;
  public keepPopularResultsOnSearch: boolean | null = null;
  public showPopularResultsIfNoResults: boolean | null = null;
  public displayStyle: string = 'block';
  public preventSubmit: boolean | null = null;

  private kevelSearchProducts: Promise<HTMLElement | null> | null = null;

  private focusOutListener: ((e: FocusEvent) => void) | null = null;

  private hasTrackedSearchInitiation: boolean = false;

  private static disposeHorizontalScrollFade: (() => void) | null = null;
  private static disposeKeyboardDismiss: (() => void) | null = null;

  constructor(config: SearchConfig) {
    Search.Instance = this;

    if (config != null) {
      Search.Instance.thresholdVolume = config.ThresholdVolume;
      Search.Instance.displayStyle = config.DisplayStyle;
    }
  }

  OnInit() {
    globalEvent({
      type: 'focusin',
      event: {
        action: 'search-input',
        callback: Search.onSearch,
      },
    });

    globalEvent({
      type: 'input',
      event: {
        action: 'search-input',
        callback: debounce(Search.onSearch, 500),
      },
    });

    document.addEventListener('keydown', Search.onGlobalKeyUp);

    globalEvent({
      type: 'click',
      event: {
        event: 'search-close',
        callback: Search.CloseSearch,
      },
    });

    globalEvent({
      type: 'click',
      event: {
        action: 'search-suggestion',
        callback: Search.onSearchSuggestion,
      },
    });

    globalEvent({
      type: 'click',
      event: {
        action: 'search-open',
        callback: Search.onSearchButtonClick,
      },
    });

    globalEvent({
      type: 'click',
      event: {
        event: 'toggle-search-result',
        callback: Search.onToggleResult,
      },
    });

    globalEvent({
      type: 'keydown',
      event: {
        event: 'toggle-search-result',
        callback: Search.onToggleResult,
      },
    });

    initSearchPlaceholderAnimations();
  }

  private static onSearchButtonClick = async (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    // trigger click
    const el = e.target as HTMLElement;
    const fromEl = el?.parentElement as HTMLElement;
    const searchInputElement = fromEl?.querySelector('[data-action="search-input"]') as HTMLInputElement;
    const inputValue = Search.Instance.searchInput?.value.trim() ?? '';

    const isSearchBarActive = Search.Instance.searchBarElement?.classList.contains('active');
    if (isSearchBarActive && inputValue) {
      const searchResultFooter = Search.Instance.searchResults?.querySelector('[data-container="search-result-footer-link"]') as HTMLElement;
      searchResultFooter?.click();
    }

    if (!searchInputElement) return false;
    await Search.initNewSearchInstance(searchInputElement);
    Search.Instance.initialized = true;
    searchInputElement.focus();

    return false;
  };

  private static onSearch = async (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const input = e.target;
    if (!(input instanceof HTMLInputElement)) return;

    const shouldBlurOnFirstMobileFocus = Search.shouldBlurOnFirstMobileFocus(input, e.type);

    if (shouldBlurOnFirstMobileFocus) {
      Search.markSearchSuggestionsAsShown(input);
      input.blur();
    }

    if (e.type === 'focusin') {
      await Search.initNewSearchInstance(input);
      Search.Instance.initialized = true;
    }

    if (e.type === 'focusin' || e.type === 'input') {
      const query = Search.Instance.searchInput?.value.trim() ?? '';
      Search.resetSearchResultAnnouncement();

      const targetAttrs = input.attributes;
      const token = targetAttrs.getNamedItem('data-token')?.value;

      if (Search.ShouldSearchBasedOnUserInput(query) && !!token) {
        await Search.GetSearchDataBasedOnUserInput(targetAttrs, token, query);

        if (!Search.Instance.keepPopularResultsOnSearch && Search.Instance.popularResults != null) {
          Search.Instance.popularResults.classList.remove('active');
        }

        Search.updateSearchResultAnnouncement();
      } else if (Search.ShouldGetFocusInData(query) && !!token) {
        if (!Search.Instance.keepPopularResultsOnSearch && Search.Instance.popularResults != null) {
          Search.Instance.popularResults.classList.remove('active');
        }
        await Search.GetFocusInData();
      }

      if (Search.ShouldHideSearchBasedOnUserInput(query) && Search.Instance.searchResults !== null) {
        Search.Instance.searchResults.classList.remove('active');
      }

      // Re-read the input because overlapping handlers may resolve out of order, and the panel must show what is typed now.
      applyPreSearchTerm(Search.Instance.popularResults, Search.Instance.searchInput?.value ?? query);
    }

    return false;
  };

  private static updateSearchResultAnnouncement = () => {
    const value: string = Search.Instance.searchInput?.value.trim() ?? '';

    announceSearchResult(Search.Instance.searchBarElement, value, Search.Instance.searchResults);
  };

  // Drops a queued announcement while the query is still changing, so screen readers only ever get
  // the message that matches the results currently rendered.
  private static resetSearchResultAnnouncement = () => {
    clearSearchResultAnnouncement(Search.Instance.searchBarElement);
  };

  private static onSearchSuggestion = async (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const element = e.target;
    if (!(element instanceof HTMLElement)) return;
    const newValue = element.getAttribute('data-value');
    if (!Search.Instance.searchInput) return;
    Search.Instance.searchInput.value = newValue ?? '';
    Search.Instance.searchInput.focus();

    return false;
  };

  private static onGlobalKeyUp = (e: KeyboardEvent) => {
    if (!Search.Instance.initialized || !Search.Instance.searchBarElement?.classList.contains('active')) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      Search.CloseSearch(e);
      return;
    }
  };

  private static ShouldSearchBasedOnUserInput(query: string) {
    return !Search.ShouldReuseLastSearch(query) && query.length >= Search.Instance.thresholdVolume;
  }

  private static ShouldHideSearchBasedOnUserInput(query: string) {
    return query.length < Search.Instance.thresholdVolume;
  }

  private static ShouldGetFocusInData(query: string) {
    return Search.Instance.showPopularResultsOnFocusIn && query.length < Search.Instance.thresholdVolume;
  }

  private static ShouldReuseLastSearch(query: string) {
    return query !== '' && (Search.Instance.pendingSearch === query || Search.Instance.lastSearchString === query);
  }

  private static shouldBlurOnFirstMobileFocus(input: HTMLInputElement, eventType: string) {
    return isMobile() && eventType === 'focusin' && input.dataset.searchSuggestionsShown !== 'true';
  }

  private static markSearchSuggestionsAsShown(input: HTMLInputElement) {
    input.dataset.searchSuggestionsShown = 'true';
  }

  private static resetSearchSuggestionsShown() {
    Search.Instance.searchInput?.removeAttribute('data-search-suggestions-shown');
  }

  private static GetSearchDataBasedOnUserInput = async (targetAttrs: NamedNodeMap, token: string, query: string) => {
    const element = document.querySelector('.subcription-interval .value[data-subscription-interval]');
    const interval = element ? element.getAttribute('data-subscription-interval') : '';

    const searchData = {
      includeArticles: parseToBoolean(targetAttrs.getNamedItem('data-include-articles')?.value ?? ''),
      includeBrands: parseToBoolean(targetAttrs.getNamedItem('data-include-brands')?.value ?? ''),
      includePages: parseToBoolean(targetAttrs.getNamedItem('data-include-pages')?.value ?? ''),
      includeProductSeries: parseToBoolean(targetAttrs.getNamedItem('data-include-productseries')?.value ?? ''),
      subscriptionProductsOnly: parseToBoolean(targetAttrs.getNamedItem('data-query-subscriptions')?.value ?? ''),
      searchTerm: query,
      subscriptionInterval: interval,
      preventSubmit: Search.Instance.preventSubmit,
    };

    Search.Instance.pendingSearch = query;

    try {
      const response = await OnSearch(searchData, token);
      const currentQuery = Search.Instance.searchInput?.value.trim() ?? '';

      if (currentQuery !== query || response.data.query !== query) {
        return;
      }

      if (response.data.success) {
        if (Search.Instance.searchResults) {
          Search.Instance.searchResults.innerHTML = Search.getOrderedSearchResultsPartial(response.data.partial);
          hideDecorativeEmoji(Search.Instance.searchResults);
          Search.Instance.searchResults.classList.add('active');
          ProductListComponent.refreshProductList(false, false);
          Search.initHorizontalScrollFade(Search.Instance.searchBarElement);
          Search.Instance.lastSearchString = query;

          const noResultContainer = Search.Instance.searchResults.querySelector('[data-no-results="true"]');
          if (noResultContainer != null && Search.Instance.showPopularResultsIfNoResults && Search.Instance.popularResults != null) {
            Search.Instance.popularResults.classList.add('active');
          } else if (Search.Instance.popularResults != null) {
            Search.Instance.popularResults.classList.remove('active');
          }
        }

        if (!searchData.subscriptionProductsOnly) {
          await Search.Instance.displayKevelData();
        }
      } else {
        logCustomMessage('Response not success in GetSearchDataBasedOnUserInput', {
          action: 'GetSearchDataBasedOnUserInput',
          responseData: response.data,
          responseQuery: response.data.query,
          responseMessage: response.data.message,
          searchInput: Search.Instance.searchInput?.value ?? 'no search input',
          stack: new Error().stack ?? '',
        });
      }
    } catch (error) {
      console.error('Error fetching search data:', error);
      logCustomMessage('Error in GetSearchDataBasedOnUserInput', {
        action: 'GetSearchDataBasedOnUserInput',
        searchInput: Search.Instance.searchInput?.value ?? 'no search input',
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? (error.stack ?? '') : '',
      });
    } finally {
      if (Search.Instance.pendingSearch === query) {
        Search.Instance.pendingSearch = null;
      }
    }
  };

  // Re-run after each render of the search results; the previous containers are disposed first.
  private static initHorizontalScrollFade = (root: HTMLElement | null = Search.Instance.popularResults) => {
    Search.disposeHorizontalScrollFade?.();
    Search.disposeHorizontalScrollFade = null;

    const fadeContainers = root?.querySelectorAll<HTMLElement>('[data-container="horizontal-fade"]');
    if (!fadeContainers?.length) return;

    Search.disposeHorizontalScrollFade = initHorizontalScrollFade(fadeContainers);
  };

  // The blur has no relatedTarget, so focusOutListener keeps the flyout open.
  private static initKeyboardDismiss = (searchBar: HTMLElement, input: HTMLInputElement) => {
    Search.removeKeyboardDismiss();

    // The wrapper, because the result panels are re-rendered on every search.
    Search.disposeKeyboardDismiss = dismissKeyboardOnDrag(searchBar, input);
  };

  private static removeKeyboardDismiss = () => {
    Search.disposeKeyboardDismiss?.();
    Search.disposeKeyboardDismiss = null;
  };

  private static GetFocusInData = async () => {
    // don't get focusIn data from server if we already have it in this instance
    if (Search.Instance.focusInPopularData != null) {
      if (Search.Instance.popularResults != null) {
        // Keep a panel that is already rendered. New cards would report their impressions again every time the panel reopens.
        if (Search.Instance.popularResults.childElementCount === 0) {
          Search.Instance.popularResults.innerHTML = Search.Instance.focusInPopularData;
          hideDecorativeEmoji(Search.Instance.popularResults);
        }
        Search.updatePopularResultsTagsState(Search.Instance.popularResults);
        Search.onFocusInContentRendered();
      }

      Search.Instance.lastSearchString = null;
      return;
    }

    // Share a request that is already running. A second response would re-render the panel and report its impressions again.
    Search.Instance.focusInDataRequest ??= Search.FetchFocusInData().finally(() => {
      Search.Instance.focusInDataRequest = null;
    });
    await Search.Instance.focusInDataRequest;
  };

  private static FetchFocusInData = async () => {
    try {
      const response = await OnGetFocusInData();
      if (Search.Instance.pendingSearch === response.data.query) {
        Search.Instance.pendingSearch = null;
      }

      if (response.data.success && Search.Instance.popularResults) {
        Search.Instance.focusInPopularData = response.data.partialPopularResults;

        // The user may have typed a query while this request was in flight; don't paint popular results over their search results.
        if (!Search.ShouldGetFocusInData(Search.Instance.searchInput?.value.trim() ?? '')) return;

        Search.Instance.popularResults.innerHTML = response.data.partialPopularResults;
        hideDecorativeEmoji(Search.Instance.popularResults);
        Search.updatePopularResultsTagsState(Search.Instance.popularResults);

        if (Search.Instance.popularResults.innerHTML !== '') {
          Search.onFocusInContentRendered();
        }
      } else if (!response.data.success) {
        logCustomMessage('Response not success in GetFocusInData', {
          action: 'GetFocusInData',
          responseData: response.data,
          responseQuery: response.data.query,
          responseMessage: response.data.message,
          stack: new Error().stack ?? '',
        });
      }
    } catch (error) {
      logCustomMessage('Error in GetFocusInData', {
        action: 'GetFocusInData',
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? (error.stack ?? '') : '',
      });
    }
  };

  private static updatePopularResultsTagsState = (popularResults: HTMLElement) => {
    popularResults.classList.toggle('popular-results-area--without-tags', popularResults.querySelector('.search-focusin__tags') == null);
  };

  private static onFocusInContentRendered = () => {
    Search.Instance.popularResults?.classList.add('active');
    Search.initHorizontalScrollFade(Search.Instance.searchBarElement);
    ProductListComponent.refreshProductList(false, false);
    // The panel is not inside a container the event handler watches for new products, so its cards are observed here.
    EasyfyModules.EasyfyEventHandler.RebindObserveProducts();
    Search.Instance.initKevelSearchSuggestionAd();
  };

  private static initNewSearchInstance = async (searchInputElement: HTMLInputElement) => {
    Search.Instance.searchBarElement = searchInputElement.closest('[data-container="search-bar"]');
    if (!Search.Instance.searchBarElement) return;
    const searchBar = Search.Instance.searchBarElement;
    Search.Instance.searchBarElement.classList.add('active');
    Search.Instance.searchInput = searchInputElement;
    setTrackedSearchInput(searchInputElement);
    Search.Instance.searchResults = Search.Instance.searchBarElement.querySelector('[data-container="search-result"]');
    Search.Instance.popularResults = Search.Instance.searchBarElement.querySelector('[data-container="popular-results"]');
    void Search.loadKevelSearchProducts();
    Search.Instance.backdrop = searchBar?.parentNode?.querySelector('[data-container="search-backdrop"]') ?? null;
    Search.Instance.ShowBackdrop();
    Search.initKeyboardDismiss(searchBar, searchInputElement);
    Search.Instance.searchForm = Search.Instance.searchBarElement.querySelector('form');
    Search.Instance.preventSubmit = parseToBoolean(searchInputElement.getAttribute('data-prevent-submit') ?? '');
    Search.Instance.showPopularResultsOnFocusIn = parseToBoolean(searchInputElement.getAttribute('data-show-popular-results') ?? '');
    Search.Instance.keepPopularResultsOnSearch = parseToBoolean(searchInputElement.getAttribute('data-keep-popular-results') ?? '');
    Search.Instance.showPopularResultsIfNoResults = parseToBoolean(searchInputElement.getAttribute('data-show-popular-results-if-no-results') ?? '');
    Search.Instance.hasTrackedSearchInitiation = await trackSearchInitiation(
      Search.Instance.hasTrackedSearchInitiation,
      parseToBoolean(searchInputElement.getAttribute('data-search-personalisation') ?? '')
    );
    Search.Instance.lastSearchString = null;

    document.body.classList.add('search-open');
    if (Search.Instance.backdrop != null && isMobile()) {
      addCss(document.body, 'noof');
    }

    if (Search.Instance.preventSubmit) Search.preventSubmit();

    if (!Search.Instance.preventSubmit) Search.preventEmptySearch();
    Search.hideSearchEventListener();

    Search.Instance.focusOutListener = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget;
      if (relatedTarget instanceof HTMLElement && Search.Instance.searchBarElement && !Search.Instance.searchBarElement.contains(relatedTarget)) {
        Search.CloseSearch(e);
      }
    };
    Search.Instance.searchBarElement.addEventListener('focusout', Search.Instance.focusOutListener);
  };

  private static hideSearchEventListener = () => {
    const backdrop = Search.Instance.backdrop;
    backdrop?.addEventListener(
      'click',
      (event: Event) => {
        if (
          event.target instanceof HTMLElement &&
          (!backdrop.contains(event.target) ||
            (event.target.hasAttribute('data-container') && event.target.getAttribute('data-container') === 'search-backdrop'))
        ) {
          Search.Instance.SearchClear();
          document.body.classList.remove('search-open');
        }
      },
      { once: true }
    );
  };

  private static CloseSearch(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    Search.resetSearchResultAnnouncement();

    if (Search.Instance.focusOutListener && Search.Instance.searchBarElement) {
      Search.Instance.searchBarElement.removeEventListener('focusout', Search.Instance.focusOutListener);
      Search.Instance.focusOutListener = null;
    }

    Search.Instance.SearchClear();

    return false;
  }

  // Keeps article and page result sections, including their tab order, in the preferred order for the current viewport.
  private static getOrderedSearchResultsPartial = (partial: string) => {
    const template = document.createElement('template');
    template.innerHTML = partial;
    Search.setOtherResultSectionOrder(template.content);

    return template.innerHTML;
  };

  private static setOtherResultSectionOrder = (searchResults: ParentNode) => {
    const otherPanel = searchResults.querySelector<HTMLElement>('#other-panel');
    const articles = otherPanel?.querySelector<HTMLElement>('.search-results__articles');
    const pages = otherPanel?.querySelector<HTMLElement>('.search-results__pages');

    if (!otherPanel || !articles || !pages) return;

    const firstSection = isDesktop() ? articles : pages;
    const secondSection = isDesktop() ? pages : articles;
    otherPanel.insertBefore(firstSection, secondSection);
  };

  private static preventEmptySearch = () => {
    Search.Instance.searchForm?.addEventListener('submit', (e: Event) => {
      if (!Search.Instance.searchInput?.value) {
        e.preventDefault();
        return;
      }
    });
  };

  private static preventSubmit = () => {
    Search.Instance.searchForm?.addEventListener('submit', (e: Event) => {
      e.preventDefault();
      return;
    });
  };

  // The Kevel card is fetched separately from the search results. Starting the fetch when the search
  // opens, instead of after the first results render, lets the results include (or drop) the section in
  // the same frame, so brands and products below it do not shift once the card arrives.
  private static loadKevelSearchProducts = (): Promise<HTMLElement | null> => {
    if (Search.Instance.kevelSearchProducts) return Search.Instance.kevelSearchProducts;

    // Only memoize once a template has been found.
    const template = Search.Instance.searchBarElement?.querySelector<HTMLTemplateElement>(KevelContainerSelector.SearchFlyoutTemplate);
    const placement = template?.content.querySelector<HTMLElement>(KevelContainerSelector.SearchFlyoutProducts);
    if (!placement || !Features.FeatureIsActive(HayppFeatureNames.Kevel)) return Promise.resolve(null);

    // Fill a detached copy of the template. That copy is cloned into each results render.
    Search.Instance.kevelSearchProducts = Kevel.Instance.fillDetachedCopy(placement).catch(() => null);

    return Search.Instance.kevelSearchProducts;
  };

  private displayKevelData = async () => {
    const searchResults = Search.Instance.searchResults;
    const kevelContainer = searchResults?.querySelector<HTMLElement>(KevelContainerSelector.SearchFlyoutContainer);
    const placement = searchResults?.querySelector<HTMLElement>(KevelContainerSelector.SearchFlyoutProducts);
    if (!kevelContainer || !placement) return;

    const kevelProducts = await Search.loadKevelSearchProducts();
    if (!kevelContainer.isConnected) return; // results were re-rendered meanwhile; that render handles its own section

    if (!kevelProducts) {
      kevelContainer.remove();
      return;
    }

    placement.replaceWith(kevelProducts.cloneNode(true));
    kevelContainer.style.display = Search.Instance.displayStyle;
    // Rebind observers after Kevel content is loaded so tracking events work
    EasyfyModules.EasyfyEventHandler.RebindObserveElements();
  };

  private initKevelSearchSuggestionAd = () => {
    const searchBarElement = Search.Instance.searchBarElement;
    if (!searchBarElement?.classList.contains('active')) return;
    if (!searchBarElement.classList.contains('header-search')) return;

    const container = searchBarElement.querySelector<HTMLElement>(KevelContainerSelector.SearchSuggestionAd);
    if (!container) return;

    initKevelSearchSuggestionAd(container);
  };

  SearchClear() {
    Search.resetSearchSuggestionsShown();
    Search.Instance.HideSearchResult();
    Search.Instance.HideBackdrop();
    Search.Instance.hasTrackedSearchInitiation = false;
    Search.Instance.pendingSearch = null;
    Search.Instance.lastSearchString = null;
    Search.Instance.initialized = false;
    if (Search.Instance.searchInput) {
      Search.Instance.searchInput.blur();
      Search.Instance.searchInput.value = '';
    }
    document.body.classList.remove('search-open');
    removeCss(document.body, 'noof');
    Search.removeKeyboardDismiss();
  }

  SearchBarInFocus(e: Event) {
    if (e.type === 'focusin') {
      if (!Search.Instance.IsEventTargetInsideSearch(e) && Search.Instance.searchResults != null) {
        Search.Instance.searchResults.classList.remove('active');
      }
    }
  }

  HideSearchResult() {
    if (Search.Instance.searchResults) {
      Search.Instance.searchResults.classList.remove('active');
    }
    Search.Instance.popularResults?.classList.remove('active');
    Search.Instance.searchBarElement?.classList.remove('active');
  }

  ShowSearchResult(e: Event) {
    if (!Search.Instance.searchResults) return;

    if (!e?.target) {
      Search.Instance.searchResults.classList.add('active');

      return;
    }

    if (!Search.Instance.IsEventTargetInsideSearch(e)) {
      Search.Instance.searchResults.classList.add('active');
    }
  }

  HideBackdrop() {
    Search.Instance.backdrop?.classList.remove('active');
  }

  ShowBackdrop() {
    Search.Instance.backdrop?.classList.add('active');
  }

  IsEventTargetInsideSearch(e: Event) {
    if (!(this.searchBarElement instanceof HTMLElement) || !(e.target instanceof Node)) {
      return false;
    }

    return this.searchBarElement.contains(e.target);
  }

  private static onToggleResult = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLButtonElement)) return;

    const searchResults = target.closest<HTMLElement>('[data-container="search-result"]');
    if (!searchResults) return;

    const toggles = Array.from(searchResults.querySelectorAll<HTMLButtonElement>('[data-event="toggle-search-result"]'));

    const enabledToggles = toggles.filter((toggle) => !toggle.disabled && toggle.getAttribute('aria-disabled') !== 'true');

    if (!enabledToggles.length) return;

    if (target.disabled) {
      event.preventDefault();
      return;
    }

    let nextIndex = enabledToggles.indexOf(target);
    if (nextIndex < 0) return;

    if (event instanceof KeyboardEvent) {
      switch (event.key) {
        case 'ArrowRight':
          nextIndex = (nextIndex + 1) % enabledToggles.length;
          break;
        case 'ArrowLeft':
          nextIndex = (nextIndex - 1 + enabledToggles.length) % enabledToggles.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = enabledToggles.length - 1;
          break;
        default:
          return;
      }
    }

    event.preventDefault();
    event.stopPropagation();

    const nextToggle = enabledToggles[nextIndex];
    const originalIndex = toggles.indexOf(nextToggle);

    Search.setActiveSearchResultSection(searchResults, originalIndex);
    nextToggle.focus();

    return false;
  };

  private static setActiveSearchResultSection = (searchResults: HTMLElement, activeIndex: number) => {
    const toggles = searchResults.querySelectorAll<HTMLElement>('[data-event="toggle-search-result"]');
    const sections = searchResults.querySelectorAll<HTMLElement>('[data-container="search-result-tab"]');
    const nextActiveIndex = activeIndex >= 0 ? activeIndex : 0;

    toggles.forEach((toggle, index) => {
      const isActive = index === nextActiveIndex;
      toggle.classList.toggle('search-results__toggle--active', isActive);
      toggle.setAttribute('aria-selected', `${isActive}`);

      if (isActive) {
        toggle.removeAttribute('tabindex');
      } else {
        toggle.setAttribute('tabindex', '-1');
      }
    });

    sections.forEach((section, index) => {
      const isActive = index === nextActiveIndex;
      section.classList.toggle('search-results__section--active', isActive);
      section.hidden = !isActive;
    });

    Search.initHorizontalScrollFade(Search.Instance.searchBarElement);
  };
}
